import { useMutation, useQuery } from '@apollo/client/react'
import MiniLogo from '@assets/images/kering-logo-min.png'
import { ChevronDown, LogOut } from 'lucide-react'
import { useCallback, useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'
import { Avatar, AvatarFallback, AvatarImage } from '~/components/ui/avatar'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '~/components/ui/dropdown-menu'
import { GQL_GET_MY_SCORM_TRACKING, GQL_UPSERT_SCORM_TRACKING } from '~/gql/scorm/scorm'
import { useAuth } from '~/helpers/store/auth'
import { createScorm12Adapter, ScormApiBridge } from '~/utils/scorm'

declare global {
  // eslint-disable-next-line ts/consistent-type-definitions
  interface Window {
    API_1484_11?: ScormApiBridge
    API?: ReturnType<typeof createScorm12Adapter>
  }
}

function ScormPlayer() {
  const { t } = useTranslation()
  const { payload } = useAuth()
  const navigate = useNavigate()
  const bridgeRef = useRef<ScormApiBridge | null>(null)
  const iframeRef = useRef<HTMLIFrameElement>(null)

  const { data, loading } = useQuery(GQL_GET_MY_SCORM_TRACKING, {
    fetchPolicy: 'network-only',
  })

  const [upsertTracking] = useMutation(GQL_UPSERT_SCORM_TRACKING)

  const handleCommit = useCallback(async (scormData: Record<string, unknown>) => {
    const input: Record<string, unknown> = {}

    if (scormData.completionStatus) input.completionStatus = scormData.completionStatus
    if (scormData.successStatus) input.successStatus = scormData.successStatus
    if (scormData.scoreRaw != null) input.scoreRaw = scormData.scoreRaw
    if (scormData.scoreMin != null) input.scoreMin = scormData.scoreMin
    if (scormData.scoreMax != null) input.scoreMax = scormData.scoreMax
    if (scormData.scoreScaled != null) input.scoreScaled = scormData.scoreScaled
    if (scormData.totalTime) input.totalTime = scormData.totalTime
    if (scormData.sessionTime) input.sessionTime = scormData.sessionTime
    if (scormData.suspendData) input.suspendData = scormData.suspendData
    if (scormData.location) input.location = scormData.location

    await upsertTracking({ variables: { input } })
  }, [upsertTracking])

  useEffect(() => {
    if (loading) return

    const initialData = data?.getMyScormTracking
      ? {
          completionStatus: data.getMyScormTracking.completionStatus as string,
          successStatus: data.getMyScormTracking.successStatus as string,
          scoreRaw: data.getMyScormTracking.scoreRaw,
          scoreMin: data.getMyScormTracking.scoreMin,
          scoreMax: data.getMyScormTracking.scoreMax,
          scoreScaled: data.getMyScormTracking.scoreScaled,
          totalTime: data.getMyScormTracking.totalTime,
          sessionTime: data.getMyScormTracking.sessionTime,
          suspendData: data.getMyScormTracking.suspendData,
          location: data.getMyScormTracking.location,
        }
      : null

    const bridge = new ScormApiBridge(initialData, handleCommit)
    bridgeRef.current = bridge

    // Mount SCORM 2004 API
    window.API_1484_11 = bridge
    // Mount SCORM 1.2 adapter
    window.API = createScorm12Adapter(bridge)

    return () => {
      delete window.API_1484_11
      delete window.API
    }
  }, [loading, data, handleCommit])

  if (loading) {
    return (
      <div className="flex h-dvh items-center justify-center bg-background">
        <p className="font-heading text-lg tracking-wide">{t('scorm.player.loading')}</p>
      </div>
    )
  }

  return (
    <div className="flex h-dvh flex-col bg-black">
      <div className="flex items-center justify-between px-6 py-3 bg-black text-white border-b border-neutral-800">
        <span className="font-heading text-xs tracking-[0.3em] uppercase">Kering</span>
        {payload && (
          <DropdownMenu>
            <DropdownMenuTrigger className="flex items-center gap-2 rounded-lg border border-neutral-600 bg-neutral-800/60 px-3 py-1.5 transition-colors hover:border-neutral-400 hover:bg-neutral-700 focus:outline-none">
              <Avatar className="size-7 rounded-full">
                <AvatarImage src={MiniLogo} alt={`${payload.firstName} ${payload.lastName}`} />
                <AvatarFallback className="rounded-full bg-neutral-700 text-xs text-white">
                  {payload.firstName[0]}
                  {payload.lastName[0]}
                </AvatarFallback>
              </Avatar>
              <span className="font-heading text-sm tracking-wide">
                {payload.firstName}
                {' '}
                {payload.lastName}
              </span>
              <ChevronDown className="size-4 text-neutral-400" />
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48 rounded-lg">
              <DropdownMenuLabel className="p-0 font-normal">
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm">
                  <Avatar className="size-8 rounded-full">
                    <AvatarImage src={MiniLogo} alt={`${payload.firstName} ${payload.lastName}`} />
                    <AvatarFallback className="rounded-full">
                      {payload.firstName[0]}
                      {payload.lastName[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight">
                    <span className="truncate font-semibold">{`${payload.firstName} ${payload.lastName}`}</span>
                    <span className="truncate text-xs text-muted-foreground">{payload.email}</span>
                  </div>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/logout')} className="cursor-pointer">
                <LogOut className="mr-2 size-4" />
                {t('common.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )}
      </div>
      <div className="flex-1">
        <iframe
          ref={iframeRef}
          src="/scorm-content/index_lms.html"
          sandbox="allow-scripts allow-same-origin allow-forms allow-popups allow-downloads"
          className="w-full h-full border-0"
          title={t('scorm.player.title')}
          allow="fullscreen"
        />
      </div>
    </div>
  )
}

export default ScormPlayer
